import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, ASPECT_RATIOS, indexById, refreshPosterSlides } from "@/lib/api";
import { setClubLogos } from "@/lib/clubLogos";
import { SlideShow } from "@/components/SlideShow";

const REFRESH_MS = 60000;
const VERSION_CHECK_MS = 5 * 60000;
const buildOf = (html) => (html.match(/static\/js\/main\.[\w]+\.js/) || [""])[0];
const fetchBuild = () => fetch(`/?v=${Date.now()}`, { cache: "no-store" }).then((r) => r.text()).then(buildOf);
const toggleFs = () => {
  const el = document.documentElement;
  if (document.fullscreenElement) document.exitFullscreen?.();
  else (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
};
const ratioOf = (r) => {
  const pad = parseFloat(ASPECT_RATIOS.find((x) => x.value === r)?.pad || "56.25");
  return 100 / pad;
};

export default function TvPlayer() {
  const { id, code } = useParams();
  const query = useSearchParams()[0].get("ecran");
  const ecran = code || query || "";
  const [banner, setBanner] = useState(null);
  const [error, setError] = useState(false);
  const [hint, setHint] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setHint(false), 6000);
    const onKey = (e) => { if (e.key === "f" || e.key === "F" || e.key === "Enter") toggleFs(); };
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); };
  }, []);

  useEffect(() => {
    let current = null;
    const check = () => fetchBuild().then((b) => {
      if (current === null) current = b;
      else if (b && b !== current) window.location.reload();
    }).catch(() => {});
    check();
    const timer = setInterval(check, VERSION_CHECK_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let sig = "";
    const load = () =>
      api.get(id ? `/public/tv/${id}` : "/public/tv-default", { params: ecran ? { ecran } : {} }).then(({ data }) => {
        const next = JSON.stringify(data);
        if (next === sig) return;
        sig = next;
        setClubLogos(data.club_logos || []);
        const b = data.banner;
        b.images = refreshPosterSlides(b.images, indexById(data.posters));
        setBanner(b);
        setError(false);
      }).catch(() => setError((e) => e || !sig));
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [id, ecran]);

  if (error) return <div className="flex h-screen items-center justify-center bg-black text-white/60" data-testid="tv-error">Bandeau introuvable</div>;
  if (!banner) return <div className="h-screen bg-black" data-testid="tv-loading" />;

  const ratio = ratioOf(banner.aspect_ratio);
  return (
    <div className="fixed inset-0 flex cursor-none items-center justify-center overflow-hidden bg-black" data-testid="tv-root"
      onClick={() => { toggleFs(); setHint(false); }}>
      {hint && (
        <div className="pointer-events-none absolute bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-black/70 px-5 py-2 text-sm text-white/90" data-testid="tv-fullscreen-hint">
          Cliquez n'importe où (ou touche F) pour le plein écran
        </div>
      )}
      <div className="relative" style={{ width: `min(100vw, ${100 * ratio}vh)`, height: `min(100vh, ${100 / ratio}vw)` }}>
        <SlideShow images={banner.images} playing loop={banner.loop !== false} showProgress={false} />
      </div>
    </div>
  );
}
